import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import { PlusIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Section } from '../../shared/ui/layout';
import { SectionPanel } from '../../shared/ui/section-panel';
import { receivedHeaders, type THeaderOrigin } from '../../entities/destination';
import { Problem } from './field-bits';
import type { TDestinationSettings } from './use-destination-settings';

const ORIGIN: Record<THeaderOrigin, string> = {
  hookpipe: 'ours',
  forwarded: 'passed on',
  static: 'added here',
  signature: 'signature',
  auth: 'credential',
};

const Preview = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, destination } = settings;
  const signing = draft.signOutbound && (destination.secretSet || Boolean(draft.secret));
  const authReady =
    draft.authType !== 'none' &&
    (Boolean(draft.authCredential) ||
      (destination.authCredentialSet && draft.authCredential !== ''));

  const rows = receivedHeaders({
    staticHeaders: draft.headers,
    forwardHeaders: draft.forwardHeaders,
    signing,
    auth: {
      type: draft.authType,
      header: draft.authHeader,
      username: draft.authUsername,
      prefix: draft.authPrefix,
    },
    authReady,
  });

  return (
    <SectionPanel
      className="mt-4"
      title={`What ${destination.name} receives`}
      note="in the order we write them"
    >
      {rows.map((row) => (
        <div
          key={`${row.origin}-${row.name}`}
          className="flex h-row items-center gap-3 border-b border-border-row px-input last:border-b-0"
        >
          <span className="w-col-destination shrink-0 truncate font-mono text-meta text-primary">
            {row.name}
          </span>
          <span
            className={cn(
              'min-w-0 grow truncate text-meta',
              row.origin === 'static' ? 'font-mono text-primary' : 'text-muted',
            )}
          >
            {row.value}
          </span>
          <span className="w-col-code shrink-0 text-right text-micro text-faint">
            {ORIGIN[row.origin]}
          </span>
        </div>
      ))}
    </SectionPanel>
  );
};

export const DestinationHeaders = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, problem } = settings;

  const setHeader = (index: number, patch: { key?: string; value?: string }) =>
    change({
      headers: draft.headers.map((row, position) =>
        position === index ? { ...row, ...patch } : row,
      ),
    });

  return (
    <Section title="Headers">
      <div className="max-w-form-narrow">
        <SectionPanel
          title="Headers we add"
          actions={
            <Button
              variant="ghost"
              size="sm"
              className="h-control-xs gap-2"
              onClick={() => change({ headers: [...draft.headers, { key: '', value: '' }] })}
            >
              <PlusIcon size={12} /> Add
            </Button>
          }
        >
          {draft.headers.length === 0 && (
            <p className="px-input py-3 text-meta text-muted">
              None added. Every delivery still carries ours — listed below.
            </p>
          )}

          {draft.headers.map((row, index) => (
            <div
              key={index}
              className="flex h-row-lg items-center gap-2 border-b border-border-row px-input last:border-b-0"
            >
              <Input
                bare
                size="sm"
                mono
                aria-label="Header name"
                placeholder="x-environment"
                className="w-col-destination shrink-0 px-0"
                value={row.key}
                onChange={(event) => setHeader(index, { key: event.target.value })}
              />
              <Input
                bare
                size="sm"
                mono
                aria-label="Header value"
                placeholder="production"
                className="min-w-0 grow px-0"
                value={row.value}
                onChange={(event) => setHeader(index, { value: event.target.value })}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-control-xs shrink-0"
                onClick={() =>
                  change({ headers: draft.headers.filter((_, position) => position !== index) })
                }
              >
                Remove
              </Button>
            </div>
          ))}
          {problem('headers') && (
            <div className="px-input pb-3">
              <Problem>{problem('headers')}</Problem>
            </div>
          )}
        </SectionPanel>

        <Preview settings={settings} />
      </div>
    </Section>
  );
};
